import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

const AdminApprovalModal = ({ isOpen, onClose, provider, onApproval }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');

  const handleApprove = async () => {
    setLoading(true);
    setError('');

    try {
      const userRef = doc(db, 'users', provider.uid);
      await updateDoc(userRef, {
        isApproved: true,
        isActive: true,
        approvedBy: 'admin', // In a real app, this would be the admin's user ID
        approvedAt: new Date(),
        approvalNotes: approvalNotes.trim() || null,
        updatedAt: new Date()
      });

      onApproval('approved');
      onClose();
    } catch (error) {
      console.error('Error approving provider:', error);
      setError('Failed to approve provider. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    setLoading(true);
    setError('');

    try {
      const userRef = doc(db, 'users', provider.uid);
      await updateDoc(userRef, {
        isApproved: false,
        isActive: false,
        rejectedBy: 'admin', // In a real app, this would be the admin's user ID
        rejectedAt: new Date(),
        rejectionNotes: approvalNotes.trim() || null,
        updatedAt: new Date()
      });

      onApproval('rejected');
      onClose();
    } catch (error) {
      console.error('Error rejecting provider:', error);
      setError('Failed to reject provider. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !provider) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Review Service Provider</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Provider Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Provider Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Name</label>
                  <p className="text-gray-800">{provider.displayName || 'Not provided'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                  <p className="text-gray-800">{provider.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Phone</label>
                  <p className="text-gray-800">{provider.phone || 'Not provided'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Business Name</label>
                  <p className="text-gray-800">{provider.businessName || 'Not provided'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Specialty</label>
                  <p className="text-gray-800">{provider.specialty || 'Not specified'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Location</label>
                  <p className="text-gray-800">{provider.location || 'Not provided'}</p>
                </div>
              </div>
              
              {provider.description && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-600 mb-1">Business Description</label>
                  <p className="text-gray-800 bg-white rounded p-3">{provider.description}</p>
                </div>
              )}
            </div>

            {/* Registration Details */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Registration Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Registration Date</label>
                  <p className="text-gray-800">
                    {provider.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Account Type</label>
                  <p className="text-gray-800 capitalize">{provider.accountType || 'basic'}</p>
                </div>
                {provider.referredBy && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Referred By</label>
                    <p className="text-gray-800">Referral Code: {provider.referralCode}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Approval Notes */}
            <div>
              <label htmlFor="approvalNotes" className="block text-sm font-medium text-gray-700 mb-2">
                Approval/Rejection Notes (Optional)
              </label>
              <textarea
                id="approvalNotes"
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                rows={3}
                placeholder="Add any notes about this provider..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4 pt-4">
              <button
                onClick={handleReject}
                disabled={loading}
                className="flex-1 bg-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Processing...' : 'Reject Application'}
              </button>
              <button
                onClick={handleApprove}
                disabled={loading}
                className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Processing...' : 'Approve Application'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminApprovalModal;
